import { BaseController } from '@maia/feature-utils';

import {
  ActivityDto,
  ActivityService,
  ClaimDto,
  ClaimService,
  CustomerDto,
  CustomerService,
  InsuranceDto,
  InsuranceService,
  InvoiceDto,
  InvoiceService,
  NoteDto,
  NoteService,
  QuotationDto,
  QuotationService,
  TaskDto,
  TaskService,
  CustomerCreateDto,
  AttachmentDto,
  AttachmentService,
} from '@maia/shared';

export class CustomerController extends BaseController {
  constructor(
    private customerService: CustomerService,
    private claimService: ClaimService,
    private insuranceService: InsuranceService,
    private invoiceService: InvoiceService,
    private quotationService: QuotationService,
    private activityService: ActivityService,
    private notesService: NoteService,
    private tasksService: TaskService,
    private attachmentService: AttachmentService,
  ) {
    super();
  }

  async create(
    params: ConstructorParameters<typeof CustomerCreateDto>[number],
  ) {
    return this.customerService.create(new CustomerCreateDto(params));
  }

  async remove(params: { guid: string; email: string }) {
    return this.customerService.remove(params);
  }

  async updateDetails(params: {
    guid: string;
    old_email: string;
    email: string;
    phone?: string;
  }) {
    return await this.customerService.updateDetails(params);
  }

  async updateIban(
    params: Parameters<typeof this.customerService.updateIban>[number],
  ) {
    return await this.customerService.updateIban(params);
  }

  async updateBillingCycle(
    params: Parameters<typeof this.customerService.updateBillingCycle>[number],
  ) {
    return await this.customerService.updateBillingCycle(params);
  }

  async updateAddress(params: Parameters<typeof this.customerService.updateAddress>[number]) {
    return await this.customerService.updateAddress(params);
  }

  async updatePersonalData(params: Parameters<typeof this.customerService.updatePersonalData>[number]) {
    return await this.customerService.updatePersonalData(params);
  }

  async impersonate(email: string) {
    return await this.customerService.impersonate(email);
  }

  async getCustomers(search: Record<string, unknown>) {
    return this.getEntities(
      this.customerService,
      { path: 'customers', search },
      CustomerDto,
    );
  }

  async getCustomer(guid: string) {
    return this.getEntity(
      this.customerService,
      `customers/${guid}`,
      CustomerDto,
    );
  }

  async getInsuranceOverview(guid: string) {
    return this.getEntity(
      this.attachmentService,
      `customers/${guid}/insurance-overview`,
      AttachmentDto,
    );
  }

  async getClaims(guid: string, search: Record<string, unknown>) {
    return this.getEntities(
      this.claimService,
      { path: `customers/${guid}/claims`, search },
      ClaimDto,
    );
  }

  async getInsurances(guid: string, search: Record<string, unknown>) {
    const response = await this.getEntities(
      this.insuranceService,
      { path: `customers/${guid}/insurances`, search },
      InsuranceDto,
    );
    return response;
  }

  async getInvoices(guid: string, search: Record<string, unknown>) {
    return this.getEntities(
      this.invoiceService,
      { path: `customers/${guid}/invoices`, search },
      InvoiceDto,
    );
  }

  async getQuotations(guid: string, search: Record<string, unknown>) {
    return this.getEntities(
      this.quotationService,
      { path: `customers/${guid}/quotations`, search },
      QuotationDto,
    );
  }

  async getActivities(guid: string, search: Record<string, unknown>) {
    return this.getEntities(
      this.activityService,
      { path: `customers/${guid}/activities`, search },
      ActivityDto,
    );
  }

  async getNotes(guid: string, search: Record<string, unknown>) {
    return this.getEntities(
      this.notesService,
      { path: `customers/${guid}/notes`, search },
      NoteDto,
    );
  }

  async getTasks(guid: string, search: Record<string, unknown>) {
    return this.getEntities(
      this.tasksService,
      { path: `customers/${guid}/tasks`, search },
      TaskDto,
    );
  }

  async createNote(
    path: Parameters<typeof this.notesService.create>[0],
    params: Parameters<typeof this.notesService.create>[1],
  ) {
    return await this.notesService.create(path, params);
  }
}
